import { logoutApi } from '@apis/cp/cp-auth.js';
import { Button, PopOver } from '@common-web/index';
import { QRCodeGenerator } from '@components-web/index';
import { ADD_PRODUCT, MANAGE_ORDERS, OMS_DASHBOARD_PATH } from '@constants/seller-center-paths.js';
import { strings } from '@constants/strings';
import { clearUserInfo } from '@reducers/cp/user-slice/index.js';
import { handleOMSLogout } from '@utils/utils';
import { Flex, Image, Layout } from 'antd';
import cx from 'clsx';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { removeCookie } from '../../../auth/helper/helper';
import PermissionsHelper from '../../../auth/helper/PermissionHelper.js';
import { Icon, Toast, Tooltip } from '../../../common/web-view';
import UserAvatar from '../../../common/web-view/avatar';
import { DASHBOARD_PATH } from '../../../constants/cp-paths.js';
import {
  ACTION_CREATE,
  ACTION_INDEX,
  ACTION_PROP_GO_QR_INFO,
  ACTION_SHOW,
  PERMISSION_KEY_ADMIN,
  PERMISSION_KEY_NOTIFICATION,
  PERMISSION_KEY_ORDER,
  PERMISSION_KEY_PRODUCT,
  PERMISSION_KEY_USER,
} from '../../../constants/permission-constants.js';
import { PROFILE_SETTINGS } from '../../../constants/seller-center-paths';
import tenantConfig from '../../../data/customizer/tenant-config';
import ConfigDB from '../../../data/customizer/theme';
import { isCP } from '../../../utils/app-utils.js';
import styles from './index.module.scss';
import Notifications from './notifications';
import { RolesToolTip } from './RolesToolTip';
import { manageOrdersHeaderClickEvent, addProductHeaderClickEvent } from '@ga-events';

const { Header } = Layout;
const logo = ConfigDB?.data?.logo;

const TopHeader = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    store = {},
    name = '-',
    email = null,
    mobile = null,
    image = null,
    roles = [],
  } = useSelector((state) => (isCP() ? state.cpUserData?.userInfo : (state.userData?.userInfo?.userProfile ?? {})));
  const [popoverVisible, setPopoverVisible] = useState(false);
  const showAddProductBtn = () => !location?.pathname?.includes('add-product');
  const showManageOrdersBtn = () => !location?.pathname?.includes('manage-orders');

  const consumerText = tenantConfig?.constants?.consumer_text;
  const { govt_registration_status } = store || {};
  const disablePointerEvents =
    !isCP() && govt_registration_status !== 'verified' && !!tenantConfig?.tax_verification_enabled;

  const handleCPLogout = async () => {
    try {
      const res = await logoutApi();
      if (res.success) {
        removeCookie('access_token');
        dispatch(clearUserInfo({ logout: true }));
      }
    } catch (error) {
      Toast.error('Error during logout:', error);
    } finally {
      setPopoverVisible(false);
    }
  };

  const popoverContent = (
    <>
      <Flex align="center" gap={8} className={styles.userInfo}>
        <UserAvatar
          src={image}
          avatarSize={48}
          style={{ fontSize: 16, backgroundColor: 'var(--ant-color-primary)', marginTop: '-2px' }}
          avatarOnly={true}
        >
          {isCP() ? (name ? name[0] : '-') : store?.name ? store?.name[0] : '-'}
        </UserAvatar>
        <Flex vertical gap={6}>
          <span className="fs-16 fw-600 lineHeight1">{isCP() ? name : store?.name}</span>
          <span className="text-dark-500 lineHeight1">{email || mobile || '-'}</span>
          <Flex gap={2} display="flex">
            <span className="gray-500 lineHeight1 fs-12">
              {!!roles?.length &&
                roles
                  ?.slice(0, 2)
                  ?.map((item, index) => item?.name + (index !== roles?.slice(0, 2)?.length - 1 ? ',' : ''))}
              {!!roles?.length && roles?.length > 2 && (
                <Tooltip title={<RolesToolTip roles={roles} />}>
                  <span className="gray-500 lineHeight1 fs-12">+{roles?.length - 2} more</span>
                </Tooltip>
              )}
            </span>
          </Flex>
        </Flex>
      </Flex>
      {isCP() &&
        !!tenantConfig?.qrcode_generation_enabled &&
        PermissionsHelper.getPermissions(PERMISSION_KEY_ADMIN, ACTION_PROP_GO_QR_INFO) && (
          <Flex align="center" gap={10} className={styles.logoutContainer}>
            <Icon icon="IconScanQr" iconSize="1.2em" />
            <QRCodeGenerator />
          </Flex>
        )}
      {!isCP() && PermissionsHelper.getPermissions(PERMISSION_KEY_USER, ACTION_SHOW) && (
        <Link to={PROFILE_SETTINGS} className={cx(styles.logoutContainer, 'flex')}>
          <Flex
            align="center"
            gap={10}
            onClick={() => {
              setPopoverVisible(false);
            }}
          >
            <Icon icon="FiUser" iconSize="1.2em" />
            <span className="fw-600">{strings?.profile_settings}</span>
          </Flex>
        </Link>
      )}

      <Flex
        align="center"
        gap={10}
        onClick={isCP() ? handleCPLogout : handleOMSLogout}
        className={styles.logoutContainer}
      >
        <Icon icon="FiLogOut" iconSize="1.2em" />
        <span className="fw-600">{strings?.logout}</span>
      </Flex>
    </>
  );

  return (
    <Header className={styles.topHeader}>
      <Flex align="end" gap={4}>
        <Link
          style={{ pointerEvents: disablePointerEvents ? 'none' : 'auto' }}
          to={isCP() ? DASHBOARD_PATH : OMS_DASHBOARD_PATH}
        >
          <Flex align="center" gap={6} style={{ paddingBottom: 10 }}>
            <Image src={logo?.src} width={logo?.width} preview={false} />
          </Flex>
        </Link>
      </Flex>
      <Flex gap={12} align="center">
        {!isCP() && (
          <>
            <Button
              text={consumerText}
              type="link"
              icon={'HiOutlineExternalLink'}
              href={process.env.REACT_APP_CONSUMER_URL}
              target="_blank"
            />
            {showManageOrdersBtn() && PermissionsHelper.getPermissions(PERMISSION_KEY_ORDER, ACTION_INDEX) && (
              <Link
                to={MANAGE_ORDERS}
                style={{ display: 'inline-flex' }}
                onClick={() => {
                  const sourcePage = location.pathname.split("/").pop();
                  manageOrdersHeaderClickEvent(sourcePage);
                }}
              >
                <Button icon={'IconOrdersList'} iconSize={18} style={{ '--icon-color': 'var(--ant-color-primary)' }}>
                  {strings?.manage_orders}
                </Button>
              </Link>
            )}
            {showAddProductBtn() && PermissionsHelper.getPermissions(PERMISSION_KEY_PRODUCT, ACTION_CREATE) && (
              <Link
                to={ADD_PRODUCT}
                style={{ display: 'inline-flex', pointerEvents: disablePointerEvents ? 'none' : 'auto' }}
                onClick={() => {
                 const sourcePage = location.pathname.split("/").pop();
                  addProductHeaderClickEvent(sourcePage);
                }}
              >
                <Button disabled={disablePointerEvents} type="primary" icon={'IconAddProduct'} iconSize={18}>
                  {strings?.add_product}
                </Button>
              </Link>
            )}
            {PermissionsHelper.getPermissions(PERMISSION_KEY_NOTIFICATION, ACTION_INDEX) && <Notifications />}
          </>
        )}
        <PopOver
          content={popoverContent}
          placement="bottomRight"
          trigger="hover"
          visible={popoverVisible}
          setVisible={setPopoverVisible}
          overlayClassName={styles.userProfileOverlay}
        >
          <Flex align="center" gap={10} className={styles.userProfilePopover}>
            <span className="fw-600">{isCP() ? (name ?? '-') : (store?.name ?? '-')}</span>
            <UserAvatar
              src={image}
              avatarSize={32}
              style={{
                fontSize: 16,
                backgroundColor: 'var(--ant-color-primary)',
                marginTop: '-2px',
              }}
              avatarOnly={true}
            >
              {isCP() ? (name ? name[0] : '-') : store?.name ? store?.name[0] : '-'}
            </UserAvatar>
          </Flex>
        </PopOver>
      </Flex>
    </Header>
  );
};

export default TopHeader;
